import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { Input, Label } from '@/shared/components';
import {
    normaliseVin,
    normaliseVrm,
    type AdditionalVehicleDetailsFormInputs,
} from '../../schemas/additionalvehicledetails.schema';

interface VehicleIdentifierFieldsProps {
    control: Control<AdditionalVehicleDetailsFormInputs>;
    watchedValues: Pick<AdditionalVehicleDetailsFormInputs, 'vin' | 'vrm'>;
    fieldIssues: Partial<Record<'vin' | 'vrm', string>>;
}

export const VehicleIdentifierFields: React.FC<VehicleIdentifierFieldsProps> = ({ control, watchedValues, fieldIssues }) => {
    return (
        <>
            <div>
                <Label htmlFor="vin">VIN</Label>
                <Controller
                    name="vin"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                        const message = error?.message ?? fieldIssues.vin;
                        const describedBy = message ? 'vin-message' : undefined;

                        return (
                            <>
                                <Input
                                    type="text"
                                    id="vin"
                                    // Read the value from watch(), not field.value: field.value
                                    // resurrects a cleared value, so value and message could disagree.
                                    value={watchedValues.vin || ''}
                                    // normaliseVin returns undefined for an empty string, so clearing
                                    // the field removes the key and counts as a repair.
                                    onChange={(e) => field.onChange(normaliseVin(e.target.value))}
                                    error={!!message}
                                    aria-invalid={!!message}
                                    aria-describedby={describedBy}
                                    placeholder="Enter vehicle VIN"
                                    autoComplete="off"
                                />
                                {message && (
                                    // role="alert" only for a post-interaction RHF error, not for a
                                    // resting fieldIssues message.
                                    <p
                                        id="vin-message"
                                        role={error ? 'alert' : undefined}
                                        className="mt-1.5 text-xs text-mp-error"
                                    >
                                        {message}
                                    </p>
                                )}
                            </>
                        );
                    }}
                />
            </div>
            <div>
                <Label htmlFor="vrm">VRM</Label>
                <Controller
                    name="vrm"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                        const message = error?.message ?? fieldIssues.vrm;
                        const describedBy = message ? 'vrm-message' : undefined;

                        return (
                            <>
                                <Input
                                    type="text"
                                    id="vrm"
                                    // Read the value from watch(), not field.value: field.value
                                    // resurrects a cleared value, so value and message could disagree.
                                    value={watchedValues.vrm || ''}
                                    // normaliseVrm returns undefined for an empty string, so clearing
                                    // the field removes the key and counts as a repair.
                                    onChange={(e) => field.onChange(normaliseVrm(e.target.value))}
                                    error={!!message}
                                    aria-invalid={!!message}
                                    aria-describedby={describedBy}
                                    placeholder="Enter vehicle registration"
                                    autoComplete="off"
                                />
                                {message && (
                                    <p
                                        id="vrm-message"
                                        role={error ? 'alert' : undefined}
                                        className="mt-1.5 text-xs text-mp-error"
                                    >
                                        {message}
                                    </p>
                                )}
                            </>
                        );
                    }}
                />
            </div>
        </>
    );
};
